import { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Quick reply options
const QUICK_REPLIES = [
  { text: "¿Cómo funciona?" },
  { text: "¿Es terapia?" },
  { text: "¿Cuánto cuesta?" }
];

// Initial greeting messages (A/B variants)
const GREETING_MESSAGES = [
  "Hola 👋 Estoy aquí para resolver tus dudas y ayudarte a agendar.",
  "¿Tienes preguntas sobre cómo funciona? Te ayudo rápido.",
  "¿Te ayudo a agendar una cita?"
];

export function ChatbotWidget({ listenerId, listenerName, onAgendarClick }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false); // For delayed entrance
  const [hasAnimated, setHasAnimated] = useState(false); // Animation only once
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Delayed entrance animation - only once
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
      // Mark as animated after animation completes
      setTimeout(() => setHasAnimated(true), 600);
    }, 3000); // 3 second delay
    
    return () => clearTimeout(timer);
  }, []);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize with greeting when opened
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const randomGreeting = GREETING_MESSAGES[Math.floor(Math.random() * GREETING_MESSAGES.length)];
      setMessages([{ role: 'assistant', content: randomGreeting }]);
    }
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Send message to backend
  const sendMessage = async (text) => {
    if (!text.trim() || isLoading) return;
    
    // Add user message
    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chatbot/message`, {
        message: text,
        conversation_id: conversationId,
        listener_id: listenerId,
        listener_name: listenerName,
        page_url: window.location.href
      });

      setConversationId(response.data.conversation_id);
      
      // Add assistant response
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response,
        isCrisis: response.data.crisis_detected
      }]);

    } catch (error) {
      console.error('Chatbot error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Disculpa, tuve un problema. ¿Puedo ayudarte con algo específico?'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle quick reply click
  const handleQuickReply = (text) => {
    sendMessage(text);
  };

  // Handle "Agendar cita" click
  const handleAgendarClick = async () => {
    // Track the action
    if (conversationId) {
      try {
        await axios.post(`${API}/chatbot/track-action?conversation_id=${conversationId}&action=clicked_agendar`);
      } catch (e) {}
    }
    
    // Close chat and trigger booking
    setIsOpen(false);
    if (onAgendarClick) {
      onAgendarClick();
    }
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  // Don't render until visible (after delay)
  if (!isVisible) return null;

  return (
    <>
      {/* CSS for entrance animation */}
      <style>{`
        @keyframes chatbotSlideIn {
          0% {
            opacity: 0;
            transform: translateY(-20px);
          }
          70% {
            opacity: 1;
            transform: translateY(3px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .chatbot-entrance {
          animation: chatbotSlideIn 0.5s ease-out forwards;
        }
      `}</style>

      {/* Floating Button - positioned above Emergent badge */}
      <div 
        className={`fixed bottom-24 right-4 z-[9999] ${!hasAnimated ? 'chatbot-entrance' : ''}`}
      >
        {/* Main button - WhatsApp green */}
        {!isOpen && (
          <button
            onClick={() => setIsOpen(true)}
            className="flex items-center gap-2 text-white pl-4 pr-5 py-3 rounded-full shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-105"
            style={{ backgroundColor: '#25D366' }}
            data-testid="chatbot-toggle"
          >
            <MessageCircle className="w-5 h-5" />
            <span className="text-sm font-medium whitespace-nowrap">Te ayudo a agendar</span>
          </button>
        )}
        
        {/* Close button when open */}
        {isOpen && (
          <button
            onClick={() => setIsOpen(false)}
            className="w-11 h-11 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-105"
            style={{ backgroundColor: '#25D366' }}
            data-testid="chatbot-toggle"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Chat Window - Compact */}
      {isOpen && (
        <div 
          className="fixed bottom-40 right-4 w-[290px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl flex flex-col z-[9999] overflow-hidden"
          style={{ maxHeight: '45vh', minHeight: '280px' }}
          data-testid="chatbot-window"
        >
          {/* Header - WhatsApp green */}
          <div className="text-white p-3 flex items-center gap-2" style={{ backgroundColor: '#25D366' }}>
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <MessageCircle className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm">Asistente Apoyum</h3>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white p-1">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-2 space-y-2 bg-white">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                    msg.role === 'user'
                      ? 'text-white rounded-br-sm'
                      : msg.isCrisis
                      ? 'bg-amber-100 text-amber-900 rounded-bl-sm'
                      : 'bg-stone-100 text-stone-800 rounded-bl-sm'
                  }`}
                  style={msg.role === 'user' ? { backgroundColor: '#25D366' } : {}}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            
            {/* Loading indicator */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-stone-100 rounded-2xl px-3 py-2">
                  <Loader2 className="w-4 h-4 text-stone-500 animate-spin" />
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Replies - Show only at start, no borders/lines */}
          {messages.length <= 1 && !isLoading && (
            <div className="px-2 pb-1 flex flex-wrap gap-1 bg-white">
              {QUICK_REPLIES.slice(0, 3).map((qr, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuickReply(qr.text)}
                  className="text-xs bg-stone-100 rounded-full px-2 py-1 hover:bg-stone-200 transition-colors"
                >
                  {qr.text}
                </button>
              ))}
            </div>
          )}

          {/* Agendar CTA */}
          <div className="px-2 pb-2 bg-white">
            <Button 
              onClick={handleAgendarClick}
              className="w-full text-white py-2 text-sm font-medium rounded-full"
              style={{ backgroundColor: '#25D366' }}
              data-testid="chatbot-agendar-btn"
            >
              Agendar cita
            </Button>
          </div>

          {/* Input Area - Clean, no border line */}
          <form onSubmit={handleSubmit} className="p-2 flex gap-2 bg-white">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Escribe aquí..."
              className="flex-1 bg-stone-100 rounded-full px-3 py-2 text-sm focus:outline-none"
              disabled={isLoading}
              data-testid="chatbot-input"
            />
            <button
              type="submit"
              disabled={!inputValue.trim() || isLoading}
              className="w-9 h-9 text-white rounded-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              style={{ backgroundColor: '#25D366' }}
              data-testid="chatbot-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

export default ChatbotWidget;
